import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiMenu, FiX, FiMail, FiPhone, FiInstagram, FiFacebook, FiTwitter, FiYoutube } = FiIcons;

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();

  // Updated navLinks to include Body Analysis Report
  const navLinks = [
    { name: 'About', path: '/about' },
    { name: 'Contact', path: '/contact' },
    { name: 'Results', path: '/results' },
    { name: 'Body Analysis Report', path: '/body-analysis' }
  ];

  const socialLinks = [
    { icon: FiInstagram, url: 'https://www.instagram.com/samsnutrihub/', label: 'Instagram' },
    { icon: FiFacebook, url: 'https://www.facebook.com/samsnutrihub/', label: 'Facebook' },
    { icon: FiTwitter, url: 'https://x.com/samsnutrihub', label: 'Twitter' },
    { icon: FiYoutube, url: 'https://www.youtube.com/@samsnutrihub', label: 'Youtube' }
  ];

  const isActive = (path) => location.pathname === path;

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <>
      {/* Top Utility Bar */}
      <div className="bg-[#FFFDF8] py-2 px-4 text-[#4A4A4A] text-sm border-b">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row justify-between items-center">
          <div className="flex items-center space-x-4 mb-1 sm:mb-0">
            <a 
              href="mailto:hello@samsnutrihub.com" 
              className="flex items-center space-x-1 hover:text-[#2E7D32] transition-colors duration-200"
            >
              <SafeIcon icon={FiMail} className="h-3 w-3" />
              <span>hello@samsnutrihub.com</span>
            </a>
            <a 
              href="tel:+919637375555" 
              className="flex items-center space-x-1 hover:text-[#2E7D32] transition-colors duration-200"
            >
              <SafeIcon icon={FiPhone} className="h-3 w-3" />
              <span>9637375555</span>
            </a>
          </div>
          <div className="flex items-center space-x-3">
            <span className="text-xs sm:text-sm hidden sm:inline">Keep Connected:</span>
            <div className="flex items-center space-x-2">
              {socialLinks.map((social) => (
                <a
                  key={social.label}
                  href={social.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-[#2E7D32] transition-colors duration-200"
                  aria-label={social.label}
                >
                  <SafeIcon icon={social.icon} className="h-4 w-4" />
                </a>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Sticky Navigation Bar */}
      <nav className={`bg-white shadow-sm sticky top-0 z-50 transition-all duration-300 ${isScrolled ? 'shadow-lg' : ''}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Logo */}
            <Link to="/" className="flex items-center space-x-2">
              <img src="https://i.ibb.co/23zdnc98/image.png" alt="Sam's Nutri Hub logo" className="h-10 w-auto" />
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center space-x-6">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`font-medium text-sm transition-all duration-200 hover:text-[#2E7D32] ${
                    isActive(link.path) ? 'text-[#2E7D32] underline' : 'text-[#222222]'
                  }`}
                >
                  {link.name}
                </Link>
              ))}
            </div>

            {/* Desktop CTAs */}
            <div className="hidden lg:flex items-center space-x-3">
              <Link
                to="/body-analysis"
                className="bg-[#2E7D32] text-white px-5 py-2 rounded-lg font-medium text-sm hover:bg-[#1B5E20] transition-colors duration-200 transform hover:translate-y-[-1px]"
              >
                Free Assessment
              </Link>
            </div>

            {/* Mobile menu button */}
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="lg:hidden p-2 text-[#222222] hover:text-[#2E7D32] focus:outline-none focus:ring-2 focus:ring-[#2E7D32] rounded"
              aria-label="Toggle menu"
            >
              <SafeIcon icon={isOpen ? FiX : FiMenu} className="h-6 w-6" />
            </button>
          </div>

          {/* Mobile Navigation */}
          <AnimatePresence>
            {isOpen && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.2 }}
                className="lg:hidden overflow-hidden border-t border-gray-200"
              >
                <div className="py-4 space-y-2">
                  {navLinks.map((link) => (
                    <Link
                      key={link.path}
                      to={link.path}
                      onClick={() => setIsOpen(false)}
                      className={`block py-2 px-4 font-medium transition-colors duration-200 ${
                        isActive(link.path)
                          ? 'text-[#2E7D32] bg-[#EAF8EC]'
                          : 'text-[#222222] hover:text-[#2E7D32] hover:bg-gray-50'
                      }`}
                    >
                      {link.name}
                    </Link>
                  ))}
                  <div className="px-4 pt-4 space-y-2">
                    <Link
                      to="/body-analysis"
                      onClick={() => setIsOpen(false)}
                      className="block w-full bg-[#2E7D32] text-white px-4 py-2 rounded-lg font-medium text-center hover:bg-[#1B5E20] transition-colors duration-200"
                    >
                      Free Assessment
                    </Link>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </nav>
    </>
  );
};

export default Navbar;